import React, { useCallback, useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * Recuadro para firmar con el dedo, el lápiz o el mouse.
 *
 * Usa Pointer Events, que cubren los tres casos con el mismo código, y
 * `touch-action: none` para que en el celular dibujar no arrastre la página.
 *
 * El lienzo se dibuja al tamaño de pantalla × devicePixelRatio (si no, la
 * firma sale borrosa en un teléfono), pero lo que se entrega es un PNG de 600
 * px de ancho sobre fondo blanco: lo justo para verse bien en el comprobante
 * y en el correo sin pasar de unas decenas de KB.
 *
 * onChange recibe el data URL de la firma, o "" cuando está vacía.
 */

const ANCHO_EXPORTADO = 600;
const TINTA = "#141210";

const FirmaCanvas = ({ onChange, invalida = false }) => {
  const canvasRef = useRef(null);
  const dibujando = useRef(false);
  const ultimo = useRef(null);
  const trazos = useRef(0);
  const [vacia, setVacia] = useState(true);

  const preparar = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { width, height } = canvas.getBoundingClientRect();
    const escala = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * escala);
    canvas.height = Math.round(height * escala);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(escala, 0, 0, escala, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = TINTA;
    ctx.lineWidth = 2.4;
  }, []);

  /* Cambiar el tamaño de un canvas lo borra: al girar el teléfono se pide
     firmar de nuevo en vez de guardar una firma deformada. */
  const limpiar = useCallback(() => {
    preparar();
    trazos.current = 0;
    setVacia(true);
    onChange("");
  }, [preparar, onChange]);

  useEffect(() => {
    preparar();
    window.addEventListener("resize", limpiar);
    return () => window.removeEventListener("resize", limpiar);
  }, [preparar, limpiar]);

  const punto = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const exportar = () => {
    const canvas = canvasRef.current;
    const salida = document.createElement("canvas");
    salida.width = ANCHO_EXPORTADO;
    salida.height = Math.round((canvas.height / canvas.width) * ANCHO_EXPORTADO);
    const ctx = salida.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, salida.width, salida.height);
    ctx.drawImage(canvas, 0, 0, salida.width, salida.height);
    return salida.toDataURL("image/png");
  };

  const empezar = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    dibujando.current = true;
    ultimo.current = punto(event);
    /* Un toque sin arrastrar deja un punto, no nada. */
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.arc(ultimo.current.x, ultimo.current.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = TINTA;
    ctx.fill();
  };

  const mover = (event) => {
    if (!dibujando.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const actual = punto(event);
    ctx.beginPath();
    ctx.moveTo(ultimo.current.x, ultimo.current.y);
    ctx.lineTo(actual.x, actual.y);
    ctx.stroke();
    ultimo.current = actual;
  };

  const terminar = () => {
    if (!dibujando.current) return;
    dibujando.current = false;
    trazos.current += 1;
    setVacia(false);
    onChange(exportar());
  };

  return (
    <div>
      <div
        className={`relative overflow-hidden rounded-xl border bg-white ${
          invalida ? "border-red-400" : "border-navy-200"
        }`}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={empezar}
          onPointerMove={mover}
          onPointerUp={terminar}
          onPointerCancel={terminar}
          onPointerLeave={terminar}
          className="block h-44 w-full cursor-crosshair touch-none sm:h-52"
          aria-label="Recuadro para dibujar tu firma"
          role="img"
        />
        {vacia ? (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-navy-300">
            Firma aquí con el dedo o el mouse
          </p>
        ) : null}
        <div className="pointer-events-none absolute inset-x-6 bottom-9 border-b border-dashed border-navy-200" />
      </div>
      <button
        type="button"
        onClick={limpiar}
        disabled={vacia}
        className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-navy-500 hover:text-navy-900 disabled:opacity-40"
      >
        <Eraser className="h-4 w-4" aria-hidden="true" />
        Borrar y firmar de nuevo
      </button>
    </div>
  );
};

export default FirmaCanvas;
